import { type PointerEvent, type ReactNode } from 'react';
import { ChartReadout, ChartTip, useActiveMark, type TipContent } from './ChartTip';
import { useMeasuredWidth } from './useMeasuredWidth';
import { useReport } from './useReport';
import './charts.css';

export interface HeatmapProps {
  /** Row headings, top to bottom ("Mon" … "Sun"). */
  rows: readonly string[];
  /** Column headings; an empty string draws no heading for that column. */
  columns: readonly string[];
  /** values[row][column], zero for none. */
  values: readonly (readonly number[])[];
  /** Spoken summary of the whole picture. */
  label: string;
  tip: (row: number, column: number, value: number) => TipContent;
  /** The legend's top end, already formatted: "3,089 pages an hour". */
  legendHigh: string;
  /** A row to emphasise from a linked chart (the weekday of the day under the pointer). */
  highlightRow?: number | null | undefined;
  /** The row under the pointer or the keyboard, for the charts linked to this one. */
  onActiveRow?: ((row: number | null) => void) | undefined;
}

/** The ramp's steps; level 0 is "none", drawn as the raised neutral, never as "a little". */
export const HEAT_STEPS = 6;

/**
 * 0 for nothing, else 1..HEAT_STEPS on a square-root scale of the largest, so one very busy hour does
 * not crush the rest of the week into the first step (round 1: weekends and the lunch dip vanished).
 */
export function heatLevel(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  return Math.min(HEAT_STEPS, Math.max(1, Math.ceil(Math.sqrt(value / max) * HEAT_STEPS)));
}

const PAD = { top: 20, left: 40, right: 8 };
const GAP = 2;

interface Layout {
  width: number;
  height: number;
  cellW: number;
  cellH: number;
  /** On a phone the grid turns: hours down, weekdays across, so each cell stays a fingertip wide. */
  turned: boolean;
  across: number;
  down: number;
}

function layoutOf(width: number, rows: number, columns: number): Layout {
  const turned = width < 480;
  const across = turned ? rows : columns;
  const down = turned ? columns : rows;
  const cellW = (width - PAD.left - PAD.right) / Math.max(1, across);
  const cellH = turned ? 22 : 28;
  return { width, height: PAD.top + down * cellH, cellW, cellH, turned, across, down };
}

/** The data's (row, column) of the cell drawn at (down, across). */
const sourceOf = (l: Layout, d: number, a: number): [number, number] =>
  l.turned ? [a, d] : [d, a];

function Cells(props: {
  values: HeatmapProps['values'];
  l: Layout;
  active: number | null;
  highlightRow: number | null | undefined;
}) {
  const { values, l, active, highlightRow } = props;
  const max = Math.max(0, ...values.flat());
  const cells: ReactNode[] = [];
  for (let d = 0; d < l.down; d += 1) {
    for (let a = 0; a < l.across; a += 1) {
      const [r, c] = sourceOf(l, d, a);
      const v = values[r]?.[c] ?? 0;
      const lit = highlightRow === null || highlightRow === undefined || highlightRow === r;
      const cls = [
        `pui-heat pui-heat--${heatLevel(v, max)}`,
        active === d * l.across + a && 'is-active',
        !lit && 'is-dim',
      ];
      cells.push(
        <rect
          key={`${d}-${a}`}
          className={cls.filter(Boolean).join(' ')}
          x={PAD.left + a * l.cellW + GAP / 2}
          y={PAD.top + d * l.cellH + GAP / 2}
          width={l.cellW - GAP}
          height={l.cellH - GAP}
          rx={3}
        />,
      );
    }
  }
  return <>{cells}</>;
}

function Headings({
  rows,
  columns,
  l,
}: {
  rows: HeatmapProps['rows'];
  columns: HeatmapProps['columns'];
  l: Layout;
}) {
  const downLabels = l.turned ? columns : rows;
  const acrossLabels = l.turned ? rows : columns;
  return (
    <>
      {downLabels.map((t, i) =>
        t ? (
          <text
            key={`d${i}`}
            x={PAD.left - 6}
            y={PAD.top + i * l.cellH + l.cellH / 2 + 4}
            textAnchor="end"
          >
            {t}
          </text>
        ) : null,
      )}
      {acrossLabels.map((t, i) =>
        t ? (
          <text
            key={`a${i}`}
            x={PAD.left + i * l.cellW + l.cellW / 2}
            y={PAD.top - 7}
            textAnchor="middle"
          >
            {t}
          </text>
        ) : null,
      )}
    </>
  );
}

function Legend({ high }: { high: string }): ReactNode {
  return (
    <div className="pui-heat-legend" aria-hidden="true">
      <span className="pui-heat-legend__swatch pui-heat--0" title="none" />
      <span>0</span>
      {Array.from({ length: HEAT_STEPS }, (_, i) => (
        <span key={i} className={`pui-heat-legend__swatch pui-heat--${i + 1}`} />
      ))}
      <span>{high}</span>
    </div>
  );
}

function pointerCell(l: Layout, e: PointerEvent<SVGSVGElement>): number | null {
  const box = e.currentTarget.getBoundingClientRect();
  const a = Math.floor((((e.clientX - box.left) / box.width) * l.width - PAD.left) / l.cellW);
  const d = Math.floor((((e.clientY - box.top) / box.height) * l.height - PAD.top) / l.cellH);
  return a >= 0 && a < l.across && d >= 0 && d < l.down ? d * l.across + a : null;
}

/**
 * A grid of rows by columns shaded on one hue, light to dark (light to bright in dark mode), drawn at
 * the frame's measured width and turned on a phone; an empty cell is the raised neutral. Hover or the
 * arrow keys show a cell; a row lights up when a linked chart points at it.
 */
export function Heatmap(props: HeatmapProps): ReactNode {
  const { rows, columns, onActiveRow } = props;
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const l = layoutOf(width, rows.length, columns.length);
  const mark = useActiveMark(l.across * l.down, { columns: l.across });
  const current = tipAt(props, l, mark.active);
  const row = current?.row ?? null;
  useReport(row, onActiveRow);
  if (rows.length === 0 || columns.length === 0) return null;
  return (
    <div className="pui-chart-frame" ref={ref}>
      <CellTip l={l} current={current ?? busiest(props)} active={mark.active} place="above" />
      <svg
        className="pui-chart pui-chart--focusable"
        width={l.width}
        height={l.height}
        viewBox={`0 0 ${l.width} ${l.height}`}
        role="img"
        aria-label={props.label}
        {...mark.focusProps}
        onPointerMove={(e) => mark.setActive(pointerCell(l, e))}
        onPointerLeave={() => mark.setActive(null)}
      >
        <Headings rows={rows} columns={columns} l={l} />
        <Cells values={props.values} l={l} active={mark.active} highlightRow={props.highlightRow} />
      </svg>
      <CellTip l={l} current={current} active={mark.active} place="over" />
      <Legend high={props.legendHigh} />
    </div>
  );
}

/** The active cell's data row and tooltip, or null. */
function tipAt(
  props: HeatmapProps,
  l: Layout,
  active: number | null,
): { row: number; tip: TipContent } | null {
  if (active === null) return null;
  const [r, c] = sourceOf(l, Math.floor(active / l.across), active % l.across);
  return { row: r, tip: props.tip(r, c, props.values[r]?.[c] ?? 0) };
}

/** The busiest cell's tooltip: what the phone's readout strip shows until a finger touches a cell. */
function busiest(props: HeatmapProps): { tip: TipContent } | null {
  const cells = props.values.flatMap((row, r) => row.map((v, c) => [r, c, v] as const));
  const best = cells.reduce<readonly [number, number, number] | null>(
    (top, cell) => (cell[2] > 0 && (top === null || cell[2] > top[2]) ? cell : top),
    null,
  );
  return best === null ? null : { tip: props.tip(best[0], best[1], best[2]) };
}

/** The tooltip over the active cell on a desk, or the readout strip above the grid on a phone. */
function CellTip(props: {
  l: Layout;
  current: { tip: TipContent } | null;
  active: number | null;
  place: 'above' | 'over';
}) {
  const { l, current, active, place } = props;
  if (place === 'above')
    return l.turned ? (
      <ChartReadout tip={current?.tip ?? null} empty="Touch a cell to read it." />
    ) : null;
  if (l.turned || !current || active === null) return null;
  const x = PAD.left + ((active % l.across) + 0.5) * l.cellW;
  return (
    <ChartTip
      tip={current.tip}
      x={x}
      y={PAD.top + Math.floor(active / l.across) * l.cellH}
      width={l.width}
    />
  );
}
